import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CompanyScope } from 'helper/products/fileViewer';
import {
  COMPANY_PARAM,
  defaultAdminScope,
  parseCompanyParam,
  readSessionScope,
  serializeCompanyParam,
  writeSessionScope,
} from 'helper/products/fileViewerCompany';
import { FOLDER_PARAM } from './useFolderPath';

export interface UseFileViewerCompanyOptions {
  /**
   * Admin com o seletor ligado (papel + feature flag). Para os demais o escopo é sempre "todas",
   * `?empresa=` sai da URL e a sessão não é usada.
   */
  isAdmin: boolean;
  /** Usuário logado (`selectUserId`): a última escolha fica na sessão dele. */
  userId: number;
}

/**
 * Empresa escolhida no "Meus Arquivos" (admin), guardada na URL (`?empresa=7` ou `?empresa=todas`)
 * como a pasta (`useFolderPath`): o link pode ser compartilhado, sobrevive ao F5 e o "voltar" do
 * navegador volta de empresa.
 *
 * - Leitura: URL > sessão (`meus-arquivos:empresa:v1:<userId>`, por aba) > padrão
 *   (`ADMIN_DEFAULT_SCOPE`: "escolher", que deixa `scope` em `null` e `needsCompany` ligado).
 *   Admin sem `empresa` mas com `pasta` (favorito de antes do seletor) abre em "todas". A
 *   restauração da sessão não reescreve a URL.
 * - Escrita: `setCompany` grava `empresa` e apaga `pasta` na mesma navegação (`push`): a empresa
 *   é o topo da hierarquia, e trocar de empresa volta à raiz dela. A escolha em vigor (da URL ou
 *   feita aqui) é a lembrada na sessão.
 * - Cliente: `scope` é sempre `'todas'`; `?empresa=` vindo de um link de admin sai da URL com
 *   `replace`, mantendo a pasta.
 */
export default function useFileViewerCompany({ isAdmin, userId }: UseFileViewerCompanyOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get(COMPANY_PARAM);
  const hasFolderParam = searchParams.has(FOLDER_PARAM);

  const scope = useMemo<CompanyScope | null>(() => {
    const fromUrl = parseCompanyParam(param, isAdmin, hasFolderParam);
    if (fromUrl !== null) return fromUrl;
    // Só o admin chega aqui: para o cliente a URL sempre resolve em "todas"
    return readSessionScope(userId) ?? defaultAdminScope();
  }, [param, isAdmin, hasFolderParam, userId]);

  // Cliente com `?empresa=` (link de admin): o parâmetro sai, sem entrada nova no histórico.
  useEffect(() => {
    if (isAdmin || param === null) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete(COMPANY_PARAM);
      return next;
    }, { replace: true });
  }, [isAdmin, param, setSearchParams]);

  // A escolha em vigor é a última lembrada nesta aba (a URL vence a sessão).
  useEffect(() => {
    if (isAdmin && scope !== null) writeSessionScope(userId, scope);
  }, [isAdmin, scope, userId]);

  const setCompany = useCallback((next: CompanyScope) => {
    if (!isAdmin) return;
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set(COMPANY_PARAM, serializeCompanyParam(next));
      params.delete(FOLDER_PARAM);
      return params;
    });
    writeSessionScope(userId, next);
  }, [isAdmin, setSearchParams, userId]);

  return {
    scope,
    setCompany,
    /** Admin ainda sem empresa escolhida: a tela pede a escolha e não pede a lista. */
    needsCompany: isAdmin && scope === null,
  };
}
